import { create } from 'zustand';

import {
  SPOTIFY_SCOPE,
  REDIRECT_URI,
  SpotifyPlaylist,
  TokenResponse,
  generateCodeVerifier,
  generateCodeChallenge,
  storeTokens,
  getStoredTokens,
  isTokenExpired,
  getTokensFromHash
} from '@/apps/Spotify/utils/helpers';
import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';

const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;

export interface Playlist {
  id: string;
  name: string;
  uri: string;
  imageUrl: string;
}

interface SpotifyState {
  playlists: Playlist[];
  selectedPlaylistId: string | null;
  loading: boolean;
  authenticated: boolean;
  logout: () => void;
  checkAuthState: () => Promise<void>;
  fetchPlaylists: () => Promise<void>;
  selectPlaylist: (playlistId: string) => Promise<void>;
  authenticate: () => Promise<void>;
  handleCallback: () => Promise<void>;
  loadInitialPlaylist: () => Promise<void>;
}

export const useSpotifyStore = create<SpotifyState>((set, get) => ({
  playlists: [],
  selectedPlaylistId: null,
  loading: false,
  authenticated: false,

  loadInitialPlaylist: async () => {
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('spotify_playlist_id')
          .eq('user_id', userId)
          .single();

        if (error) throw error;
        if (data?.spotify_playlist_id) {
          set({ selectedPlaylistId: data.spotify_playlist_id });
        }
      });
    } catch (error) {
      handleException(error);
    }
  },

  checkAuthState: async () => {
    try {
      const { accessToken, refreshToken } = getStoredTokens();

      if (accessToken) {
        if (!isTokenExpired()) {
          set({ authenticated: true });
        } else if (refreshToken) {
          await refreshAccessToken();
          set({ authenticated: true });
        }
      }
    } catch (error) {
      handleException(error);
      set({ authenticated: false });
    }
  },

  authenticate: async () => {
    try {
      const codeVerifier = generateCodeVerifier(128);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      localStorage.setItem('spotify_code_verifier', codeVerifier);

      const authUrl = new URL('https://accounts.spotify.com/authorize');
      const params = {
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: REDIRECT_URI,
        scope: SPOTIFY_SCOPE,
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
        show_dialog: 'true',
      };

      authUrl.search = new URLSearchParams(params).toString();
      window.location.href = authUrl.toString();
    } catch (error) {
      handleException(error);
    }
  },

  fetchPlaylists: async () => {
    set({ loading: true });
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch('https://api.spotify.com/v1/me/playlists', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error(`Failed to fetch playlists: ${response.statusText}`);

      const data = await response.json();
      const playlists: Playlist[] = data.items.map((item: SpotifyPlaylist) => ({
        id: item.id,
        name: item.name,
        uri: item.uri,
        imageUrl: item.images[0]?.url,
      }));

      set({ playlists });
    } catch (error) {
      handleException(error);
      set({ playlists: [] });
    } finally {
      set({ loading: false });
    }
  },

  selectPlaylist: async (playlistId: string) => {
    const previousPlaylistId = get().selectedPlaylistId;
    set({ selectedPlaylistId: playlistId });

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('user_preferences')
          .upsert(
            { user_id: userId, spotify_playlist_id: playlistId },
            { onConflict: 'user_id' }
          );

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if the server update fails
      set({ selectedPlaylistId: previousPlaylistId });
      handleException(error);
    }
  },

  handleCallback: async () => {
    const { code } = getTokensFromHash();
    if (!code) throw new Error('No code found in URL');

    const codeVerifier = localStorage.getItem('spotify_code_verifier');
    if (!codeVerifier) throw new Error('No code verifier found');

    try {
      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: CLIENT_ID,
          grant_type: 'authorization_code',
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) throw new Error('Failed to get access token');

      const data: TokenResponse = await response.json();
      storeTokens(data.access_token, data.refresh_token, data.expires_in);
      localStorage.removeItem('spotify_code_verifier');
      set({ authenticated: true });

      window.location.href = window.location.origin + window.location.pathname;
    } catch (error) {
      handleException(error);
      throw error;
    }
  },

  logout: () => {
    // Clear stored tokens
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiry');

    // Reset store state
    set({
      authenticated: false,
      playlists: [],
      selectedPlaylistId: null,
      loading: false,
    });
  },
}));

// Helper function to refresh the access token
const refreshAccessToken = async () => {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) throw new Error('No refresh token available');

  try {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: CLIENT_ID,
      }),
    });

    if (!response.ok) throw new Error('Failed to refresh token');

    const data: TokenResponse = await response.json();
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return data.access_token;
  } catch (error) {
    handleException(error);
    throw error;
  }
}

// Helper function to get a valid access token
const getValidAccessToken = async () => {
  const { accessToken } = getStoredTokens();

  if (!accessToken || isTokenExpired()) {
    return await refreshAccessToken();
  }

  return accessToken;
}
